import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { inr, pct } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import AppLayout from "../components/AppLayout";
import { cagrCalc, xirrCalc, simpleReturnCalc, sipCalc, compoundCalc } from "../lib/calc";
import { Button } from "../components/ui/button";

const TABS = ["CAGR", "XIRR", "Simple Return", "SIP", "Compound Growth", "Withdrawal"];

const In = ({ label, value, onChange, type = "number", testId, placeholder }) => (
  <div className="space-y-1.5">
    <label className="label-caps">{label}</label>
    <input type={type} value={value} placeholder={placeholder} data-testid={testId}
      onChange={(e) => onChange(e.target.value)} className="glass-input" />
  </div>
);

const Result = ({ label, value, testId, accent = true }) => (
  <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3.5">
    <p className="label-caps">{label}</p>
    <p className={`tnum text-lg font-semibold mt-1.5 ${accent ? "text-emerald-300" : "text-white"}`} data-testid={testId}>{value}</p>
  </div>
);

const Note = ({ text = "Illustrative / Estimated figures only — not guaranteed returns." }) => (
  <p className="text-[11px] text-zinc-600 mt-3" data-testid="calc-disclaimer">{text}</p>
);

const fmtPct = (v) => (v === null || v === undefined || Number.isNaN(v) ? "—" : pct(v));

function CagrCalc() {
  const [f, setF] = useState({ initial: "", final: "", years: "" });
  const v = cagrCalc(Number(f.initial), Number(f.final), Number(f.years));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <In label="Initial Investment (₹)" value={f.initial} onChange={(x) => setF({ ...f, initial: x })} testId="cagr-initial" />
        <In label="Current / Final Value (₹)" value={f.final} onChange={(x) => setF({ ...f, final: x })} testId="cagr-final" />
        <In label="Investment Period (Years)" value={f.years} onChange={(x) => setF({ ...f, years: x })} testId="cagr-years" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Result label="CAGR" value={fmtPct(v)} testId="cagr-result" />
      </div>
    </div>
  );
}

function XirrCalc() {
  const [rows, setRows] = useState([{ date: "", amount: "" }, { date: "", amount: "" }]);
  const v = xirrCalc(rows);
  const set = (i, k) => (x) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: x } : r)));
  return (
    <div className="space-y-4">
      <p className="text-[11px] text-zinc-500">Enter investments as negative amounts and withdrawals/returns as positive amounts, with actual dates.</p>
      <div className="space-y-2" data-testid="xirr-rows">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <In label={i === 0 ? "Cash Flow Date" : ""} type="date" value={r.date} onChange={set(i, "date")} testId={`xirr-date-${i}`} />
            <In label={i === 0 ? "Cash Flow Amount (₹)" : ""} value={r.amount} onChange={set(i, "amount")} testId={`xirr-amount-${i}`} />
            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`xirr-remove-${i}`}
              className="h-10 px-3 text-xs text-zinc-500 hover:text-red-400 border border-white/10 rounded-lg transition-colors duration-200">✕</button>
          </div>
        ))}
      </div>
      <button onClick={() => setRows([...rows, { date: "", amount: "" }])} data-testid="xirr-add"
        className="text-xs font-semibold text-emerald-400 border border-emerald-500/25 rounded-lg px-3.5 py-2 hover:bg-emerald-500/10 transition-colors duration-200">
        + Add Cash Flow
      </button>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Result label="XIRR" value={fmtPct(v)} testId="xirr-result" />
      </div>
    </div>
  );
}

function SimpleCalc() {
  const [f, setF] = useState({ invested: "", current: "" });
  const r = simpleReturnCalc(Number(f.invested), Number(f.current));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <In label="Investment Amount (₹)" value={f.invested} onChange={(x) => setF({ ...f, invested: x })} testId="sr-invested" />
        <In label="Current Value (₹)" value={f.current} onChange={(x) => setF({ ...f, current: x })} testId="sr-current" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Result label="Gain / Loss" value={f.invested ? inr(r.gain) : "—"} testId="sr-gain" />
        <Result label="Return %" value={f.invested ? fmtPct(r.pct) : "—"} testId="sr-pct" />
      </div>
    </div>
  );
}

const FREQS = [["Monthly", 12], ["Quarterly", 4], ["Annually", 1]];

function SipCalc() {
  const [f, setF] = useState({ amount: "", freq: 12, rate: "", years: "" });
  const r = sipCalc(Number(f.amount) || 0, Number(f.freq), Number(f.rate) || 0, Number(f.years) || 0);
  const ready = f.amount && f.years;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <In label="Investment Amount (₹)" value={f.amount} onChange={(x) => setF({ ...f, amount: x })} testId="sip-amount" />
        <div className="space-y-1.5">
          <label className="label-caps">Frequency</label>
          <select value={f.freq} onChange={(e) => setF({ ...f, freq: e.target.value })} data-testid="sip-freq"
            className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
            {FREQS.map(([l, v]) => <option key={l} value={v}>{l}</option>)}
          </select>
        </div>
        <In label="Expected / Indicative Return (% p.a.)" value={f.rate} onChange={(x) => setF({ ...f, rate: x })} testId="sip-rate" />
        <In label="Tenure (Years)" value={f.years} onChange={(x) => setF({ ...f, years: x })} testId="sip-years" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Result label="Invested Amount" value={ready ? inr(r.invested) : "—"} testId="sip-invested" accent={false} />
        <Result label="Estimated Value" value={ready ? inr(r.value) : "—"} testId="sip-value" />
        <Result label="Estimated Growth" value={ready ? inr(r.growth) : "—"} testId="sip-growth" />
      </div>
      <Note />
    </div>
  );
}

function CompoundCalc() {
  const [f, setF] = useState({ principal: "", rate: "", freq: 12, years: "" });
  const r = compoundCalc(Number(f.principal) || 0, Number(f.rate) || 0, Number(f.freq), Number(f.years) || 0);
  const ready = f.principal && f.years;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <In label="Principal (₹)" value={f.principal} onChange={(x) => setF({ ...f, principal: x })} testId="cg-principal" />
        <In label="Rate (% p.a.)" value={f.rate} onChange={(x) => setF({ ...f, rate: x })} testId="cg-rate" />
        <div className="space-y-1.5">
          <label className="label-caps">Compounding Frequency</label>
          <select value={f.freq} onChange={(e) => setF({ ...f, freq: e.target.value })} data-testid="cg-freq"
            className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
            {FREQS.map(([l, v]) => <option key={l} value={v}>{l}</option>)}
          </select>
        </div>
        <In label="Tenure (Years)" value={f.years} onChange={(x) => setF({ ...f, years: x })} testId="cg-years" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Result label="Future Value" value={ready ? inr(r.value) : "—"} testId="cg-value" />
        <Result label="Estimated Growth" value={ready ? inr(r.growth) : "—"} testId="cg-growth" />
      </div>
      <Note />
    </div>
  );
}

function WithdrawalCalc() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [wtype, setWtype] = useState("Capital");
  const [amount, setAmount] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") api.get("/admin/investors").then((r) => setClients(r.data)).catch(() => {});
    if (user?.role === "wrm") api.get("/wrm/overview").then((r) => setClients(r.data.clients || [])).catch(() => {});
  }, [user]);

  const run = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter a valid withdrawal amount."); return; }
    if (user?.role !== "investor" && !clientId) { toast.error("Select a client."); return; }
    setBusy(true);
    try {
      const payload = { withdrawal_type: wtype, amount: Number(amount) };
      if (user?.role !== "investor") payload.investor_id = clientId;
      const r = await api.post("/withdrawal/simulate", payload);
      setRes(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const bd = res?.breakdown;
  const w = res?.withdrawal;

  return (
    <div className="space-y-4">
      <p className="text-[11px] text-zinc-500">Uses the same withdrawal engine as the Transaction Command Center — 90-day capital lock, withdrawal-option eligibility, exit load and profit sharing rules included.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {user?.role !== "investor" && (
          <div className="space-y-1.5">
            <label className="label-caps">Client</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="wc-client"
              className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
              <option value="">Select client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name} ({c.investor_code})</option>)}
            </select>
          </div>
        )}
        <div className="space-y-1.5">
          <label className="label-caps">Withdrawal Type</label>
          <select value={wtype} onChange={(e) => setWtype(e.target.value)} data-testid="wc-type"
            className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
            <option value="Capital">Capital Withdrawal</option>
            <option value="Return">Interest / Profit Withdrawal</option>
          </select>
        </div>
        <In label="Gross Withdrawal Amount (₹)" value={amount} onChange={setAmount} testId="wc-amount" />
      </div>
      <Button onClick={run} disabled={busy} data-testid="wc-run"
        className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
        {busy ? "Calculating..." : "Calculate Net Payable"}
      </Button>
      {res && (
        <div className="space-y-4" data-testid="wc-result">
          <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${res.eligible
            ? "border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-300"
            : "border-amber-500/25 bg-amber-500/[0.06] text-amber-300"}`} data-testid="wc-eligibility">
            {res.eligible ? "Eligible for withdrawal under current rules." : res.reason}
          </div>
          {w?.available && (
            <p className="text-[11px] text-zinc-500 tnum">
              Option: {w.withdrawal_option} · Capital: {inr(w.capital)} · Portfolio: {inr(w.current_value)} · Withdrawable profit: {inr(w.withdrawable_profit)}
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Result label="Gross Withdrawal" value={inr(bd.gross)} testId="wc-gross" accent={false} />
            <Result label={`Exit Load (${bd.exit_load_pct}%)`} value={inr(bd.exit_load_amount)} testId="wc-exit" accent={false} />
            <Result label={`Profit Sharing (${bd.profit_sharing_pct}%)`} value={inr(bd.profit_sharing_amount)} testId="wc-ps" accent={false} />
            <Result label="Net Payable" value={inr(bd.net_payable)} testId="wc-net" />
            {res.remaining_capital !== null && res.remaining_capital !== undefined && (
              <Result label="Remaining Capital" value={inr(res.remaining_capital)} testId="wc-rem-capital" accent={false} />
            )}
            {res.remaining_value !== null && res.remaining_value !== undefined && (
              <Result label="Remaining Portfolio Value" value={inr(res.remaining_value)} testId="wc-rem-value" accent={false} />
            )}
          </div>
          <Note text="Simulation only — no transaction is created. Actual processing follows admin authorisation." />
        </div>
      )}
    </div>
  );
}

export default function Calculators() {
  const [tab, setTab] = useState("CAGR");
  return (
    <AppLayout title="Financial Calculators" subtitle="Planning tools powered by the same financial engine as your portfolio.">
      <div className="fade-up space-y-5 max-w-4xl">
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="calc-tabs">
          {TABS.map((t) => (
            <button key={t} data-testid={`calc-tab-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setTab(t)}
              className={`text-xs font-semibold rounded-full px-4 py-2 border whitespace-nowrap transition-colors duration-200 ${
                tab === t ? "pill-active" : "border-white/10 text-zinc-500 hover:text-zinc-200"
              }`}>
              {t}
            </button>
          ))}
        </div>
        <div className="glass-card p-7" data-testid="calc-panel">
          {tab === "CAGR" && <CagrCalc />}
          {tab === "XIRR" && <XirrCalc />}
          {tab === "Simple Return" && <SimpleCalc />}
          {tab === "SIP" && <SipCalc />}
          {tab === "Compound Growth" && <CompoundCalc />}
          {tab === "Withdrawal" && <WithdrawalCalc />}
        </div>
      </div>
    </AppLayout>
  );
}
